import { Anchor, Button } from '@studio/design-system/button';
import type { HtmlAttributes } from '@studio/design-system/props';
import type { EventHandler } from '@studio/react-utils/event';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { Icon } from '#~/auto-tracking/components/icon/index.js';
import { Contingency } from '#~/room/components/common/index.js';
import type { ContingencyProps } from '#~/room/components/common/index.js';
import { useSettings } from '#~/account/components/hooks.js';
import { Panel } from '#~/account/components/settings/store/types.js';

/** @internal */
const Inner = ({ className, ...props }: Omit<ContingencyProps, 'icon'>): ReactElement => (
  <Contingency
    className={twMerge('mx-auto h-full max-w-[300px] justify-center', className)}
    icon={Icon}
    {...props}
  />
);

/** `<Empty>` component. */
export const Empty = (props: HtmlAttributes): ReactElement => (
  <Inner {...props} message='You must first add a video source in order to use auto tracking.' />
);

/** `<Uncalibrated>` component. */
export const Uncalibrated = ({
  onCalibrate,
  ...props
}: HtmlAttributes<{ onCalibrate: EventHandler }>): ReactElement => (
  <Inner
    {...props}
    message={
      <>
        This camera must be <Anchor onClick={onCalibrate}>calibrated</Anchor> before you can use
        auto-tracking.
      </>
    }
  />
);

/** `<Unsupported>` component. */
export const Unsupported = (props: HtmlAttributes): ReactElement => (
  <Inner
    {...props}
    message="The video source you've selected doesn't support this functionality."
  />
);

/** `<AutoTrackingComingSoon>` component. */
export const AutoTrackingComingSoon = (props: HtmlAttributes): ReactElement => (
  <Inner {...props} message='Auto Tracking Coming Soon' />
);

export const NotOnPlan = (props: HtmlAttributes): ReactElement => {
  const settings = useSettings();

  return (
    <Inner
      {...props}
      message={
        <div>
          <div className='mb-8'>Upgrade to unlock auto-tracking.</div>

          <Button
            onClick={() => {
              settings(Panel.Billing);
            }}
            className='mx-auto'
          >
            Upgrade Plan
          </Button>
        </div>
      }
    />
  );
};
